import React, { useMemo, useState } from 'react';
import { SessionState, MaterializedView, ScenarioDefinition } from '@/types';
import { People, Mail, Close, TrendingUp, TrendingDown } from '@mui/icons-material';

interface ContactsViewProps {
    session: SessionState;
    materializedView: MaterializedView;
    scenario?: ScenarioDefinition | null;
    onSendEmail?: (to: string) => void;
    initialSelectedNpcId?: string | null;
}

// Professional headshot photos for NPCs (shared with dashboard)
const NPC_AVATARS: Record<string, string> = {
    // Inbox Crisis
    'jennifer-martinez': 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
    'marcus-johnson': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
    'sarah-chen': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
    // The Handover
    'priya-devi': 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face',
    'aanya-joshi': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
    'hugo-lindqvist': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face',
    'greg-whittaker': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=face',
};

interface NpcContact {
    id: string;
    name: string;
    email: string;
    role: string;
}

// Contacts now derive from the active scenario at render time — no hardcoded list.

const getSenderColor = (senderId: string): string => {
    const colors = [
        'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
        'bg-orange-600', 'bg-amber-600', 'bg-teal-600', 'bg-cyan-600',
    ];
    let hash = 0;
    for (let i = 0; i < senderId.length; i++) {
        hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
};

const getTrustColor = (trust: number): string => {
    if (trust >= 0.6) return 'bg-green-500';
    if (trust >= 0.4) return 'bg-amber-500';
    return 'bg-red-500';
};

const getTrustLabel = (trust: number): string => {
    if (trust >= 0.6) return 'Positive';
    if (trust >= 0.4) return 'Neutral';
    return 'Strained';
};

const getEscalationBadge = (level: number): { label: string; color: string } | null => {
    if (level === 1) return { label: 'Awaiting response', color: 'bg-amber-500/20 text-amber-400' };
    if (level >= 2) return { label: 'Frustrated', color: 'bg-red-500/20 text-red-400' };
    return null;
};

const ContactsView: React.FC<ContactsViewProps> = ({ materializedView, scenario, onSendEmail, initialSelectedNpcId }) => {
    // Derived from scenario.npcs — was previously hardcoded to inbox-crisis NPCs.
    const npcs = useMemo<NpcContact[]>(
        () => (scenario?.npcs ?? []).map((n) => ({
            id: n.id,
            name: n.name,
            email: n.email || `${n.id}@example.com`,
            role: n.role || '',
        })),
        [scenario]
    );
    const initialContact = initialSelectedNpcId ? npcs.find(n => n.id === initialSelectedNpcId) || null : null;
    const [selectedContact, setSelectedContact] = useState<NpcContact | null>(initialContact);
    const relationships = materializedView.relationships || {};

    return (
        <div className="flex flex-col h-full bg-slate-800 text-white">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-600">
                <div className="flex items-center gap-3">
                    <People className="text-blue-400" />
                    <div>
                        <h2 className="text-lg font-bold text-white">Contacts</h2>
                        <p className="text-xs text-slate-300">{npcs.length} key contacts</p>
                    </div>
                </div>
            </div>

            {/* Contact list */}
            <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-1 gap-3">
                    {npcs.map((npc) => {
                        const rel = relationships[npc.id];
                        const trust = rel?.trust ?? 0.5;
                        const frustration = rel?.frustration ?? 0;
                        const escalation = getEscalationBadge(rel?.escalationLevel ?? 0);

                        return (
                            <button
                                key={npc.id}
                                onClick={() => setSelectedContact(npc)}
                                className="flex items-center gap-4 bg-slate-900/80 border border-slate-600 p-4 rounded-xl hover:bg-slate-700 hover:border-blue-500/40 transition-all group text-left"
                            >
                                {/* Avatar */}
                                <div className="relative shrink-0">
                                    {NPC_AVATARS[npc.id] ? (
                                        <img src={NPC_AVATARS[npc.id]} alt={npc.name}
                                            className="size-12 rounded-full object-cover border border-slate-600 group-hover:ring-2 group-hover:ring-primary/30 transition-all" />
                                    ) : (
                                        <div className={`size-12 rounded-full flex items-center justify-center text-sm font-bold text-white ${getSenderColor(npc.id)} group-hover:ring-2 group-hover:ring-primary/30 transition-all`}>
                                            {getInitials(npc.name)}
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors">
                                        {npc.name}
                                    </h4>
                                    <p className="text-[11px] text-slate-400 truncate">{npc.role}</p>
                                    {escalation && (
                                        <span className={`inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${escalation.color}`}>
                                            {escalation.label}
                                        </span>
                                    )}
                                </div>

                                {/* Trust indicator */}
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                    <span className="text-[10px] text-slate-400 font-medium">{getTrustLabel(trust)}</span>
                                    <div className="w-16 h-1.5 bg-black/40 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${getTrustColor(trust)} transition-all`}
                                            style={{ width: `${trust * 100}%` }}
                                        />
                                    </div>
                                    {frustration > 0.3 && (
                                        <span className="text-[10px] text-red-400/70 flex items-center gap-0.5">
                                            <TrendingDown style={{ fontSize: '10px' }} />
                                            Frustrated
                                        </span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Contact Detail Modal */}
            {selectedContact && (
                <div className="absolute inset-0 z-50 bg-black/60 flex items-center justify-center p-6">
                    <div className="bg-slate-900 border border-slate-600 rounded-2xl max-w-md w-full p-6 relative">
                        <button
                            onClick={() => setSelectedContact(null)}
                            className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded-lg transition-colors"
                        >
                            <Close fontSize="small" className="text-slate-300" />
                        </button>

                        {/* Avatar + Name */}
                        <div className="flex items-center gap-4 mb-6">
                            {NPC_AVATARS[selectedContact.id] ? (
                                <img src={NPC_AVATARS[selectedContact.id]} alt={selectedContact.name}
                                    className="size-16 rounded-full object-cover border-2 border-slate-600" />
                            ) : (
                                <div className={`size-16 rounded-full flex items-center justify-center text-lg font-bold text-white ${getSenderColor(selectedContact.id)}`}>
                                    {getInitials(selectedContact.name)}
                                </div>
                            )}
                            <div>
                                <h3 className="text-xl font-bold text-white">{selectedContact.name}</h3>
                                <p className="text-sm text-slate-300">{selectedContact.role}</p>
                                <p className="text-xs text-slate-400 font-mono">{selectedContact.email}</p>
                            </div>
                        </div>

                        {/* Relationship Status */}
                        {(() => {
                            const rel = relationships[selectedContact.id];
                            const trust = rel?.trust ?? 0.5;
                            const frustration = rel?.frustration ?? 0;
                            const lastInteraction = rel?.lastInteraction;
                            const escalation = getEscalationBadge(rel?.escalationLevel ?? 0);

                            return (
                                <div className="space-y-4">
                                    {/* Trust bar */}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-xs text-slate-400 font-medium">Trust Level</span>
                                            <span className="text-xs font-bold text-white flex items-center gap-1">
                                                <TrendingUp style={{ fontSize: '14px' }} className={trust >= 0.5 ? 'text-green-400' : 'text-red-400'} />
                                                {getTrustLabel(trust)}
                                            </span>
                                        </div>
                                        <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${getTrustColor(trust)} transition-all`}
                                                style={{ width: `${trust * 100}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Frustration bar (only if > 0) */}
                                    {frustration > 0 && (
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs text-slate-400 font-medium">Frustration</span>
                                                <span className="text-xs font-bold text-red-400">
                                                    {Math.round(frustration * 100)}%
                                                </span>
                                            </div>
                                            <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full bg-red-500 transition-all"
                                                    style={{ width: `${frustration * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Last interaction */}
                                    {lastInteraction && (
                                        <div className="text-xs text-slate-400">
                                            Last contacted via <span className="text-slate-300 font-medium">{lastInteraction}</span>
                                        </div>
                                    )}

                                    {/* Escalation badge */}
                                    {escalation && (
                                        <div className={`text-xs font-bold px-3 py-2 rounded-lg ${escalation.color} text-center`}>
                                            {escalation.label}
                                        </div>
                                    )}

                                    {/* Send Email button */}
                                    {onSendEmail && (
                                        <button
                                            onClick={() => {
                                                onSendEmail(selectedContact.email);
                                                setSelectedContact(null);
                                            }}
                                            className="w-full flex items-center justify-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 text-blue-400 font-bold text-sm py-3 rounded-lg transition-colors"
                                        >
                                            <Mail fontSize="small" />
                                            Send Email
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsView;
